import { Injectable } from '@angular/core';
import {
    HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LoggingService } from './logging.service';
import { ErrorPopupService } from './error-popup.service';

const DEFAULT_SERVER_ERROR = 'An unexpected error occurred. Please contact support with the reference ID.';

/**
 * Generate an RFC4122 v4 UUID (mirrors the helper in auth.interceptor.ts).
 * Used as the per-request correlation id.
 */
function generateUuid(): string {
    const cryptoObj: any = (window as any).crypto || (window as any).msCrypto;
    if (cryptoObj?.randomUUID) { return cryptoObj.randomUUID(); }
    if (cryptoObj?.getRandomValues) {
        const buf = new Uint8Array(16);
        cryptoObj.getRandomValues(buf);
        buf[6] = (buf[6] & 0x0f) | 0x40;
        buf[8] = (buf[8] & 0x3f) | 0x80;
        const hex = Array.from(buf).map(b => b.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/**
 * On a 5xx the API returns only a generic message plus `requestId` (security
 * finding M7). Components show `err.error.error` / `err.error.message` in their
 * snackbars, so the reference id is appended to those two fields here — every
 * screen then shows it without changing the ~60 components.
 *
 * Only plain JSON bodies are touched: Blob/ArrayBuffer bodies (file downloads)
 * and HTML/text error pages from a proxy are passed through unchanged.
 */
export function withReference(error: HttpErrorResponse, fallbackId: string): HttpErrorResponse {
    const body = error.error;
    if (error.status < 500 || !body || typeof body !== 'object' || Object.getPrototypeOf(body) !== Object.prototype) {
        return error;
    }
    const reference = body.requestId || error.headers?.get('X-Request-Id') || fallbackId;
    const suffix = ` (Reference: ${reference})`;
    const append = (text: unknown) =>
        typeof text === 'string' && !text.includes(reference) ? text + suffix : text;

    return new HttpErrorResponse({
        error: { ...body, error: append(body.error), message: append(body.message), requestId: reference },
        headers: error.headers,
        status: error.status,
        statusText: error.statusText,
        url: error.url ?? undefined,
    });
}

/**
 * Stamps every outbound request with an X-Request-Id (matching the backend's
 * requestLogger, which echoes the same header) and logs API failures to
 * LoggingService with that id — so a browser-side failure can be traced to the
 * exact backend request in Kibana/OpenSearch. For 5xx it also adds the id to the
 * error message the user sees (see withReference) and opens the app-wide error
 * popup with it (ErrorPopupService), so the id shows even on screens that display
 * a fixed text or nothing at all.
 *
 * Registered before PayloadCryptoInterceptor, so error bodies arrive here already
 * decrypted. Skips the /client-logs endpoint itself to avoid an infinite logging loop.
 */
@Injectable()
export class LoggingInterceptor implements HttpInterceptor {
    constructor(private logging: LoggingService, private errorPopup: ErrorPopupService) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (req.url.includes('/client-logs')) {
            return next.handle(req);
        }

        const requestId = generateUuid();
        const tracedReq = req.clone({ setHeaders: { 'X-Request-Id': requestId } });

        return next.handle(tracedReq).pipe(
            catchError((error: HttpErrorResponse) => {
                this.logging.error(`API request failed: ${req.method} ${req.url}`, {
                    requestId,
                    url: req.url,
                    name: `HttpError ${error.status}`,
                    context: {
                        source: 'LoggingInterceptor',
                        method: req.method,
                        status: error.status,
                        statusText: error.statusText,
                    },
                });
                if (error instanceof HttpErrorResponse && error.status >= 500) {
                    const body = error.error;
                    const reference = body?.requestId || error.headers?.get('X-Request-Id') || requestId;
                    const serverText = typeof body?.error === 'string' ? body.error
                        : typeof body?.message === 'string' ? body.message : null;
                    this.errorPopup.show(serverText || DEFAULT_SERVER_ERROR, reference);
                }
                return throwError(() => (error instanceof HttpErrorResponse ? withReference(error, requestId) : error));
            }),
        );
    }
}
